import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { FlaskConical, Send, Loader2 } from 'lucide-react';
import { PostCard } from '@/components/legacy/LegacyChannel';

const API_URL = window.location.origin;

export const LegacyLab = ({ user }) => {
  const [posts, setPosts] = useState([]);
  const [labels, setLabels] = useState([]);
  const [content, setContent] = useState('');
  const [label, setLabel] = useState('');
  const [posting, setPosting] = useState(false);
  const [filter, setFilter] = useState('all');

  const fetchLab = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/legacy/lab`);
      setPosts(res.data.posts);
      setLabels(res.data.labels || []);
    } catch (error) {
      console.error('Failed to load Legacy Lab:', error);
    }
  };

  useEffect(() => { fetchLab(); }, []);

  const handlePost = async () => {
    if (!label) { toast.error('Choose how you want the circle to respond'); return; }
    if (!content.trim()) { toast.error('Share what you are working through'); return; }
    setPosting(true);
    try {
      await axios.post(`${API_URL}/api/legacy/lab/posts`, { content, response_label: label });
      toast.success('Shared with the Lab 🧪');
      setContent('');
      setLabel('');
      fetchLab();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post');
    } finally {
      setPosting(false);
    }
  };

  const visible = filter === 'all' ? posts : posts.filter(p => p.response_label === filter);

  return (
    <div className="space-y-6" data-testid="legacy-lab">
      <Card className="card-base border-terracotta/20 bg-gradient-to-br from-terracotta/5 to-transparent">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-1">
            <FlaskConical className="h-5 w-5 text-terracotta" />
            <p className="text-xs uppercase tracking-widest text-terracotta">Legacy Lab</p>
          </div>
          <p className="font-heading text-xl text-forest">Bring your half-formed ideas, big decisions, and bold dreams — and tell us how to show up for you.</p>
        </CardContent>
      </Card>

      {/* Composer */}
      <Card className="card-base">
        <CardContent className="p-4 space-y-3">
          <p className="text-xs text-gray-500 uppercase tracking-wide">How do you want the circle to respond?</p>
          <div className="flex flex-wrap gap-1.5">
            {labels.map(l => (
              <button key={l} onClick={() => setLabel(l)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${label === l ? 'bg-terracotta text-white border-terracotta' : 'border-sand text-gray-600 hover:border-terracotta'}`}
                data-testid={`lab-label-${l.toLowerCase().replace(/\s+/g, '-')}`}>
                {l}
              </button>
            ))}
          </div>
          <Textarea value={content} onChange={(e) => setContent(e.target.value)}
            placeholder="What are you building, deciding, or dreaming about?" rows={3} data-testid="lab-content-input" />
          <div className="flex justify-end">
            <Button onClick={handlePost} disabled={posting} className="bg-forest hover:bg-forest/90 text-white" data-testid="lab-submit-btn">
              {posting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />} Bring It to the Lab
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => setFilter('all')} className={`text-xs px-3 py-1.5 rounded-full border ${filter === 'all' ? 'bg-forest text-white border-forest' : 'border-sand text-gray-500'}`} data-testid="lab-filter-all">
          All
        </button>
        {labels.map(l => (
          <button key={l} onClick={() => setFilter(l)} className={`text-xs px-3 py-1.5 rounded-full border ${filter === l ? 'bg-forest text-white border-forest' : 'border-sand text-gray-500'}`}>
            {l}
          </button>
        ))}
      </div>

      {/* Feed */}
      {visible.length > 0 ? (
        <div className="space-y-4">
          {visible.map(post => (
            <div key={post.id}>
              <Badge className="bg-terracotta/10 text-terracotta text-xs mb-1 ml-1" data-testid={`lab-post-label-${post.id}`}>{post.response_label}</Badge>
              <PostCard post={post} user={user} channel="lab" onRefresh={fetchLab} />
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center text-sm text-gray-400 py-6">The Lab is quiet. Be the first to bring an idea.</p>
      )}
    </div>
  );
};

export default LegacyLab;
